// src/Components/GoogleMap/InteractionLayer.js

import { useEffect, useRef } from 'react';
import { useMapState, MAP_MODES } from '../../Contexts/MapContext';

export default function InteractionLayer({ map, google, activeMenu, selectedMarker }) {
  const { mode, selectedFeature, visualDraft } = useMapState();

  const activeMarkerRef = useRef(null);
  const animationFrameId = useRef(null);
  const startTimeRef = useRef(null);
  const vertexMarkersRef = useRef([]); // Added for shape vertices

  // --- SAFETY KILL SWITCH ---
  const isForbiddenMenu = activeMenu === 'HOME' || activeMenu === 'VIEW_INFO';

  const createMarkerContent = (iconUrl) => {
    const container = document.createElement('div');
    container.className = 'interaction-marker-container';
    container.style.position = 'absolute';

    // AMENDED: Ensure the ghost marker itself uses grab/grabbing, never a finger pointer
    container.style.cursor = 'grab';

    container.style.marginLeft = '-19.2px';
    container.style.marginTop = '-38.4px';

    container.addEventListener('mousedown', (e) => e.preventDefault());

    const wrapper = document.createElement('div');
    wrapper.className = 'marker-spin-wrapper';
    wrapper.style.width = '38.4px';
    wrapper.style.height = '38.4px';
    wrapper.style.display = 'flex';
    wrapper.style.justifyContent = 'center';
    wrapper.style.alignItems = 'center';
    wrapper.style.transformStyle = 'preserve-3d';

    // AMENDED: Kill pointer events on internal elements of the ghost
    wrapper.style.pointerEvents = 'none';

    const img = document.createElement('img');
    img.src = iconUrl;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'contain';
    img.draggable = false;
    img.style.pointerEvents = 'none';

    wrapper.appendChild(img);
    container.appendChild(wrapper);
    return container;
  };

  const startSpinning = (element) => {
    if (!element) return;
    if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    startTimeRef.current = null;
    const duration = 1000;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;

      if (elapsed >= duration) {
        element.style.transform = 'rotateY(0deg)';
        animationFrameId.current = null;
        return;
      }
      const rotation = ((elapsed % duration) / duration) * 360;
      if (element) element.style.transform = `rotateY(${rotation}deg)`;

      animationFrameId.current = requestAnimationFrame(animate);
    };
    animationFrameId.current = requestAnimationFrame(animate);
  };

  const stopSpinning = () => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    if (activeMarkerRef.current && activeMarkerRef.current.content) {
      const wrapper =
        activeMarkerRef.current.content.querySelector('.marker-spin-wrapper');
      if (wrapper) wrapper.style.transform = 'rotateY(0deg)';
    }
  };

  useEffect(() => {
    if (!map || !google || !google.maps) return;

    const isCreationMode = mode === MAP_MODES.CREATE_MARKER;
    const isEditingMode = mode === MAP_MODES.EDIT_MARKER;
    const isActive = !isForbiddenMenu && (isCreationMode || isEditingMode);

    if (isActive && selectedFeature && selectedFeature.position) {
      const position = selectedFeature.position;
      const iconUrl = visualDraft?.markerIcon || selectedFeature.icon;

      if (!activeMarkerRef.current) {
        const content = createMarkerContent(iconUrl);

        activeMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          map: map,
          position: position,
          content: content,
          gmpDraggable: true,
          zIndex: 2000, // Keep above Manager markers
        });

        if (isCreationMode) {
          const wrapper = content.querySelector('.marker-spin-wrapper');
          startSpinning(wrapper);
        }
      } else {
        activeMarkerRef.current.position = position;
        const currentImg = activeMarkerRef.current.content.querySelector('img');
        if (currentImg && currentImg.src !== iconUrl) {
          activeMarkerRef.current.content = createMarkerContent(iconUrl);
        }
        if (!activeMarkerRef.current.map) activeMarkerRef.current.map = map;

        if (!isCreationMode) {
          stopSpinning();
        }
      }
    } else {
      if (activeMarkerRef.current) {
        stopSpinning();
        activeMarkerRef.current.map = null;
        activeMarkerRef.current = null;
      }
    }

    return () => {
      stopSpinning();
      if (activeMarkerRef.current) {
        activeMarkerRef.current.map = null;
        activeMarkerRef.current = null;
      }
    };
  }, [map, google, mode, selectedFeature, visualDraft, isForbiddenMenu]);

  // --- NEW EFFECT: SHAPE VERTEX RENDERING ---
  useEffect(() => {
    if (!map || !google || !google.maps) return;

    // 1. Clear any existing custom vertices
    vertexMarkersRef.current.forEach((marker) => {
      marker.map = null;
    });
    vertexMarkersRef.current = [];

    // 2. Determine if we should render vertices
    const type = (selectedMarker?.type || '').toLowerCase();
    const isShape = type === 'polygon' || type === 'line' || type === 'polyline';

    // We only want custom visual vertices in non-editable views.
    // In MARKUP_MARKERS, the native editable: true provides its own draggable vertices.
    const showCustomVertices =
      isShape && ['HOME', 'VIEW_INFO', 'UPDATE_INFO', 'MARKUP_INFO'].includes(activeMenu);

    if (showCustomVertices && selectedMarker) {
      let coords = [];

      // Extract coordinates based on shape type
      if (type === 'polygon' && selectedMarker.paths) {
        if (Array.isArray(selectedMarker.paths[0])) {
          selectedMarker.paths.forEach((ring) => coords.push(...ring));
        } else {
          coords.push(...selectedMarker.paths);
        }
      } else if ((type === 'line' || type === 'polyline') && selectedMarker.path) {
        coords.push(...selectedMarker.path);
      }

      // Generate a marker for every extracted coordinate
      coords.forEach((pt) => {
        if (!pt) return;

        const lat = typeof pt.lat === 'function' ? pt.lat() : parseFloat(pt.lat);
        const lng = typeof pt.lng === 'function' ? pt.lng() : parseFloat(pt.lng);

        if (!isNaN(lat) && !isNaN(lng)) {
          const div = document.createElement('div');

          // Set to 7px footprint
          div.style.width = '7px';
          div.style.height = '7px';
          div.style.backgroundColor = '#00B0FF'; // Blue fill
          div.style.border = '1px solid #000000'; // Thin black border
          div.style.borderRadius = '50%'; // Circle shape
          div.style.boxShadow = '0px 0px 2px rgba(0,0,0,0.5)'; // Subtle shadow

          // AdvancedMarkerElement anchors at the bottom-center.
          // Content = 7px, Border = 1px top + 1px bottom. Total height = 9px.
          // Translate Y by 4.5px (half height) to perfectly bisect the path line.
          div.style.transform = 'translateY(4.5px)';
          div.style.pointerEvents = 'none'; // Don't block map clicks

          const vertexMarker = new google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: { lat, lng },
            content: div,
            zIndex: 3000, // Keep floating above the shape itself
          });

          vertexMarkersRef.current.push(vertexMarker);
        }
      });
    }

    // Cleanup on unmount or when dependencies change (like deselection)
    return () => {
      vertexMarkersRef.current.forEach((marker) => {
        if (marker) marker.map = null;
      });
      vertexMarkersRef.current = [];
    };
  }, [map, google, selectedMarker, activeMenu]);

  return null;
}
